/* =====================================================================
 * Message history cache
 *
 * Provides an in-memory TTL + LRU cache around the conversation message
 * loader. Guarantees:
 *   - at most one in-flight request per conversationId (de-dup)
 *   - serves recent results (< 5s) from memory to avoid redundant loads
 *     across rapid conversation switches
 *   - caps total recent entries at 24 to bound memory usage
 *
 * Extracted from chatStore.ts to keep module-scope request coalescing
 * state out of the main Zustand store file.
 * ===================================================================== */

import type { ChatMessage } from '@auw/workspace-utils';

const messageHistoryInFlight = new Map<string, Promise<ChatMessage[]>>();
const messageHistoryRecent = new Map<string, { ts: number; messages: ChatMessage[] }>();

const RECENT_MESSAGE_TTL_MS = 5_000;
const MAX_RECENT_MESSAGE_HISTORY_ENTRIES = 24;

function pruneRecentMessageHistory(now = Date.now()): void {
  for (const [conversationId, recent] of messageHistoryRecent.entries()) {
    if (now - recent.ts >= RECENT_MESSAGE_TTL_MS) {
      messageHistoryRecent.delete(conversationId);
    }
  }

  if (messageHistoryRecent.size <= MAX_RECENT_MESSAGE_HISTORY_ENTRIES) return;

  const overflow = messageHistoryRecent.size - MAX_RECENT_MESSAGE_HISTORY_ENTRIES;
  const victims = [...messageHistoryRecent.entries()]
    .sort((a, b) => a[1].ts - b[1].ts)
    .slice(0, overflow);

  victims.forEach(([conversationId]) => {
    messageHistoryRecent.delete(conversationId);
  });
}

export function fetchMessageHistory(conversationId: string): Promise<ChatMessage[]> {
  const now = Date.now();
  pruneRecentMessageHistory(now);
  const recent = messageHistoryRecent.get(conversationId);
  if (recent && now - recent.ts < RECENT_MESSAGE_TTL_MS) {
    return Promise.resolve(recent.messages);
  }

  const existing = messageHistoryInFlight.get(conversationId);
  if (existing) return existing;

  const next = import('@auw/api-client')
    .then(({ loadMessages }) => loadMessages(conversationId))
    .then((messages) => {
      messageHistoryRecent.set(conversationId, { ts: Date.now(), messages });
      pruneRecentMessageHistory();
      return messages;
    })
    .finally(() => {
      if (messageHistoryInFlight.get(conversationId) === next) {
        messageHistoryInFlight.delete(conversationId);
      }
    });

  messageHistoryInFlight.set(conversationId, next);
  return next;
}

export function clearMessageHistoryCache(): void {
  messageHistoryInFlight.clear();
  messageHistoryRecent.clear();
}
